/**
 * Grila de pachete pentru frontend — DERIVATĂ, nu scrisă de mână.
 *
 * Sursa unică de adevăr e tabela Supabase `pricing_packages`
 * (backend/migrations/pricing_packages.sql). Fișierul `./packages.json` de
 * lângă e o copie GENERATĂ a acelei tabele — CRA nu poate importa din afara
 * lui `src/`, iar prețurile trebuie să existe la BUILD time (JSON-LD,
 * prerender, snapshots publice).
 *
 * Ca să schimbi un preț sau o limită:
 *   1. UPDATE în `pricing_packages` (Supabase SQL Editor).
 *   2. `python backend/scripts/sync_packages.py` — regenerează packages.json.
 *   3. Rebuild frontend.
 * NU edita cifre în acest fișier și nici în packages.json.
 *
 * Prețurile trebuie să coincidă cu cele din Stripe: `stripe_price_id` stă tot
 * în tabelă, deci ambele capete ale checkout-ului pleacă din aceeași linie.
 */
import PACKAGES from './packages.json';

/** 21% TVA RO — vine din tabelă, per pachet; expus aici pentru compatibilitate. */
export const VAT_RATE = PACKAGES.packages[0]?.vat_rate ?? 0.21;

/** Formatare RON cu separator de mii (1290 → "1.290"). */
export const formatRon = (amount) =>
  new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(amount);

/** Construiește blocul de afișaj pentru un pachet din tabelă. */
const toPlan = (pkg) => {
  const excl = pkg.price_excl_vat;
  const hasPrice = excl !== null && excl !== undefined;
  const inclVat = hasPrice ? Math.round(excl * (1 + pkg.vat_rate)) : null;
  const f = pkg.features || {};
  return {
    id: pkg.key,
    name: pkg.display_name,
    scans_per_month: pkg.adhoc_scans,
    brands_max: pkg.max_brands,
    engines: f.scan_llms,
    questions_per_scan: f.scan_questions,
    features: f,
    checkout: pkg.checkout,
    currency: pkg.currency,
    vat_rate: pkg.vat_rate,
    interval: pkg.period,
    // Prețuri pre-formatate — nicio componentă nu re-formatează.
    amount_excl_vat: excl,
    amount_incl_vat: inclVat,
    display_price: hasPrice
      ? (excl === 0 ? '0 RON' : `${formatRon(excl)} RON`)
      : 'Custom',
    display_excl_vat: hasPrice ? `${formatRon(excl)} RON + TVA` : 'Custom',
    display_excl_vat_short: hasPrice ? `${formatRon(excl)} RON` : 'Custom',
    display_incl_vat: hasPrice ? `${formatRon(inclVat)} RON` : null,
    display_per_month: hasPrice ? `${formatRon(excl)} RON + TVA / luna` : null,
    // Pachetul „de la X" (Custom): CIFRA, nu propozitia. Textul din jur („de la",
    // „+ TVA", „starting from", „+ VAT") e limba, deci sta in i18n — pana in
    // 2026-08-18 fraza se construia aici, in romana, si ajungea asa si pe /en.
    from_amount_excl_vat: pkg.from_price_excl_vat ?? null,
    from_price: pkg.from_price_excl_vat
      ? `${formatRon(pkg.from_price_excl_vat)} ${pkg.currency}`
      : null,
    // LEGACY, romana hardcodata. Ramane DOAR pentru suprafetele de continut
    // RO-only care nu trec prin i18n (src/content/competitors.js → VersusPage,
    // scripts/build-prerender.mjs prin scripts/lib/pricing.js). Nu-l folosi in
    // pagini bilingve: acolo se paseaza `from_price` catre t().
    display_from: pkg.from_price_excl_vat
      ? `de la ${formatRon(pkg.from_price_excl_vat)} RON + TVA`
      : null,
    // JSON-LD: listăm suma cu TVA, cea plătită efectiv.
    json_ld_amount: hasPrice ? String(inclVat) : null,
    json_ld_currency: pkg.currency,
  };
};

/** Toate pachetele, în ordinea din tabelă (sort_order). */
export const PLANS = PACKAGES.packages.map(toPlan);

/** Acces după cheie: PLAN_BY_ID.professional.display_excl_vat */
export const PLAN_BY_ID = Object.fromEntries(PLANS.map((p) => [p.id, p]));

/**
 * Cheie de tier → nume comercial. SINGURA mapare din frontend; echivalentul
 * din backend e TIER_DISPLAY_NAMES (core/business_config.py), derivat din
 * aceeași tabelă. Un tier necunoscut cade pe cheia brută, nu pe string gol.
 */
export const TIER_LABELS = Object.fromEntries(
  PACKAGES.packages.map((p) => [p.key, p.display_name])
);

export const tierLabel = (tier) => TIER_LABELS[tier] || tier || '';

/** Planurile cumpărabile self-serve (au price în Stripe). */
export const CHECKOUT_PLANS = PLANS.filter((p) => p.checkout);

/** Ordinea de upgrade — identică cu _TIER_HIERARCHY din usage_service.py. */
export const TIER_HIERARCHY = PACKAGES.packages.map((p) => p.key);

// ── Cheia pachetului implicit + pachetele pe ofertă ─────────────────
// Componentele scriau `'free'` de mână („e gratuit?", „arată bannerul de
// upgrade?"). Redenumirea sau înlocuirea pachetului implicit în tabelă ar fi
// lăsat condițiile alea mereu false, fără nicio eroare vizibilă.

/** Pachetul pe care cade orice cont nou. Din tabelă (`default_tier`). */
export const DEFAULT_TIER = PACKAGES.default_tier || PACKAGES.packages[0]?.key || '';

const normTier = (tier) => String(tier || '').toLowerCase();

/** Contul e pe pachetul implicit (gratuit) — singurul de pe care upsellul pleacă de la zero. */
export const isDefaultTier = (tier) => normTier(tier) === DEFAULT_TIER;

/**
 * Pachetele pe ofertă = cele care costă bani, fie cu preț listat, fie „de la"
 * (Custom, pe contract). Sunt exact pachetele care produc un abonament sau o
 * factură — deci cele pentru care are sens să arăți administrare de plată.
 */
export const OFFERED_PLANS = PLANS.filter(
  (p) => (p.amount_excl_vat ?? 0) > 0 || (p.from_amount_excl_vat ?? 0) > 0
);

const OFFERED_TIERS = new Set(OFFERED_PLANS.map((p) => p.id));

/** Tier-ul e un pachet plătit din grilă. Un tier necunoscut = `false`, nu „plătit". */
export const isOfferedTier = (tier) => OFFERED_TIERS.has(normTier(tier));

// ── Compatibilitate cu importurile existente ────────────────────────
// Componentele vechi importă constante nominale; le păstrăm ca alias-uri
// către intrările din tabelă, ca să nu existe două surse de adevăr.
export const FREE_PLAN = PLAN_BY_ID.free;
export const START_PRICE = PLAN_BY_ID.start;
export const PROFESSIONAL_PRICE = PLAN_BY_ID.professional;
export const ENTERPRISE_PLAN = {
  ...PLAN_BY_ID.enterprise,
  cta_label: 'Solicita oferta',
};

export default PLANS;
